import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { nanoid } from 'nanoid';
import { useEditor, EditorContent } from "@tiptap/react";
import { Box } from "@mui/material";
import { useAppSelector } from "@/state/hooks";
import {
    convertMermaidCodeBlocks
} from "./extensions";
import { buildEditorExtensions } from "./editorExtensions";
import TiptapToolbar from "./TiptapToolbar";
import { parseContent } from "./parseContent";
import {
    getHostDocumentBridge,
    getCleanHtml
} from "@/components/aiBridge";
import "./TiptapEditor.scss";
import type * as Y from "yjs";
import type { HocuspocusProvider } from "@hocuspocus/provider";
import { ySyncPluginKey } from "y-prosemirror";
import { electSeeder } from "@/collab/seeding";
import { presenceTarget, useActivityPublisher } from "@/collab/activityAwareness";

interface TiptapEditorProps {
    title: string;
    description: string;
    value?: string;
    onChange?: (markdown: string) => void;
    // Jira export props
    projectId?: number;
    featureId?: string;
    userStoryId?: string;
    sectionId?: number;
    workProductTitle?: string;
    hasJiraLink?: boolean; // Whether the project has a Jira integration linked
    hasAzureDevOpsLink?: boolean; // Whether the project has an Azure DevOps integration linked
    // Rebuild props
    needsRebuild?: boolean;
    onRebuild?: () => void;
    workProductId?: number;
    // Export props
    allSections?: Array<{ id: string; title: string; markdown: string; sort_order?: number }>;
    // Navigate to the Integrations view — wired to SyncPanel's "Reconnect" CTA (see Task 16).
    onNavigateToIntegrations?: () => void;
    // Real-time collaboration (Yjs). When set, content flows through the
    // fragment: no initial content, no value-reset, onChange = local edits only.
    collab?: TiptapCollab;
    // Fired with cleaned HTML when REMOTE peers change the doc (collab only).
    onRemoteChange?: (html: string) => void;
    readOnly?: boolean;
    presenceProvider?: HocuspocusProvider;
    presenceSectionId?: number | string;
}

export interface TiptapCollab {
    fragment: Y.XmlFragment;
    field: string;
    provider: HocuspocusProvider;
    synced: boolean;
}

function backfillBridgeNodeIds(editor: any): void {
    let dirty = false;
    let tr = editor.state.tr;
    editor.state.doc.descendants((node: any, pos: number) => {
        if (node.type.name === "mermaid" && !node.attrs.nodeId) {
            tr = tr.setNodeAttribute(pos, "nodeId", `d-${nanoid(8)}`);
            dirty = true;
            return false;
        }
        if (node.type.name === "table" && !node.attrs.nodeId) {
            tr = tr.setNodeAttribute(pos, "nodeId", `t-${nanoid(8)}`);
            dirty = true;
            return false;
        }
        return true;
    });
    if (dirty) {
        editor.view.dispatch(tr);
    }
}

/**
 * TiptapEditor - A modern, stable rich text editor based on ProseMirror
 *
 * Features:
 * - Markdown input/output (GitHub Flavored Markdown)
 * - Rich text editing (headings, lists, bold, italic, etc.)
 * - Image support
 * - Code blocks with syntax highlighting
 * - Tables
 * - Extensible architecture
 */
export default function TiptapEditor({
    title,
    description,
    value = "",
    onChange,
    projectId,
    featureId,
    userStoryId,
    sectionId,
    workProductTitle,
    hasJiraLink,
    hasAzureDevOpsLink,
    needsRebuild,
    onRebuild,
    workProductId,
    allSections,
    onNavigateToIntegrations,
    collab,
    onRemoteChange,
    readOnly = true,
    presenceProvider,
    presenceSectionId
}: TiptapEditorProps) {
    // Get auth token for image uploads
    const token = useAppSelector(state => state.clientToken.token);
    const [focused, setFocused] = useState(false);
    const activityTarget = presenceTarget("section", presenceSectionId ?? collab?.field.replace(/^section-/, ""));
    useActivityPublisher(
        presenceProvider ?? (collab?.synced ? collab.provider : undefined),
        activityTarget,
        !readOnly && focused ? "editing" : "viewing"
    );

    // Host-document bridge — module-level singleton. The editor registers
    // itself on mount so the agent can read/edit; unregisters on unmount.
    // Safe to call regardless of whether the AI chat panel is currently
    // rendered — the adapter holds at most one editor and is harmless when
    // nobody is listening.
    const bridge = getHostDocumentBridge();

    // Parse initial content (HTML or Markdown) - memoized to only run once
    const initialHtml = useMemo(() => parseContent(value), []);

    const editor = useEditor({
        editable: !readOnly,
        extensions: buildEditorExtensions({
            mode: "mounted",
            token: token || "",
            collab: collab ? { fragment: collab.fragment } : undefined
        }),
        content: collab ? undefined : initialHtml, // Yjs owns content in collab mode
        editorProps: {
            attributes: {
                class: "tiptap-editor-content",
                spellcheck: "true"
            }
        },
        onUpdate: ({ editor, transaction }) => {
            // Remote Yjs transactions carry the y-sync plugin meta. They must
            // not hit onChange (which triggers this client's autosave) — the
            // author's client saves its own edits.
            if (transaction.getMeta(ySyncPluginKey)) {
                if (onRemoteChange) {
                    onRemoteChange(getCleanHtml(editor));
                }
                return;
            }
            if (onChange) {
                // Serialize to HTML to preserve all rich text formatting (colors, fonts, alignment, etc.)
                // Markdown doesn't support these features, so we must save as HTML.
                // getCleanHtml strips agent staging artifacts (suggestion blocks +
                // suggestion/pending-replace marks) so unaccepted proposals never
                // round-trip to the backend.
                const html = getCleanHtml(editor);
                console.log("[TiptapEditor] Saving as HTML:", html.substring(0, 200));

                // Debug: Check if mermaid diagrams are being saved with code and dimensions
                if (html.includes("data-mermaid")) {
                    console.log("[TiptapEditor] ✓ Mermaid diagram detected in saved HTML");
                    // Extract and log the data-code attribute (now base64 encoded)
                    const codeMatch = html.match(/data-code="([^"]+)"/);
                    if (codeMatch) {
                        let displayCode = codeMatch[1].substring(0, 100);
                        // If base64 encoded, decode for display
                        if (html.includes('data-code-encoding="base64"')) {
                            try {
                                displayCode = decodeURIComponent(escape(atob(codeMatch[1]))).substring(0, 100);
                            } catch { /* show raw */ }
                        }
                        console.log("[TiptapEditor] ✓ Mermaid code saved:", displayCode);
                    } else {
                        console.warn("[TiptapEditor] ⚠ Mermaid diagram found but no data-code attribute!");
                    }

                    // Extract and log the dimensions
                    const widthMatch = html.match(/data-width="([^"]+)"/);
                    const heightMatch = html.match(/data-height="([^"]+)"/);
                    if (widthMatch && heightMatch) {
                        console.log("[TiptapEditor] ✓ Mermaid dimensions saved:", {
                            width: widthMatch[1],
                            height: heightMatch[1]
                        });
                    } else {
                        console.warn("[TiptapEditor] ⚠ Mermaid diagram found but missing dimensions!");
                    }
                }

                // Debug: Check if SVG elements are being saved with code and dimensions
                if (html.includes("data-svg")) {
                    console.log("[TiptapEditor] ✓ SVG element detected in saved HTML");
                    // Extract and log the dimensions
                    const widthMatch = html.match(/data-svg-width="([^"]+)"/);
                    const heightMatch = html.match(/data-svg-height="([^"]+)"/);
                    if (widthMatch && heightMatch) {
                        console.log("[TiptapEditor] ✓ SVG dimensions saved:", {
                            width: widthMatch[1],
                            height: heightMatch[1]
                        });
                    } else {
                        console.warn("[TiptapEditor] ⚠ SVG element found but missing dimensions!");
                    }
                }

                onChange(html);
            }
        }
    });

    // Register this editor with the host-document bridge. Each tab switch in
    // DocSectionContainer reinitializes TiptapEditor (via `key={editorKey}`),
    // so the adapter sees one editor at a time. Other call sites of
    // TiptapEditor (which may not be inside the chat layout) still register
    // — the registration is cheap and silently ignored if no chat is mounted.
    useEffect(() => {
        if (!editor || readOnly) return;
        const docTitle = title || workProductTitle || "Untitled Section";
        const stableId = sectionId ?? `${workProductId ?? "wp"}-${docTitle}`;
        const unregister = bridge.adapter.register(editor, stableId, docTitle);
        return unregister;
    }, [editor, bridge, readOnly, sectionId, title, workProductId, workProductTitle]);

    useEffect(() => {
        if (typeof editor?.setEditable === "function") editor.setEditable(!readOnly);
    }, [editor, readOnly]);

    // Older content may predate bridge node IDs. Backfill once on mount so
    // read_open_document can expose stable ids for node-level edits.
    useEffect(() => {
        if (!editor || readOnly) return;
        backfillBridgeNodeIds(editor);
    }, [editor, readOnly]);

    // Collab: advertise which section this editor has open, and seed the
    // fragment from the loaded HTML exactly once per room (guarded by the
    // collabMeta flag + lowest-clientID election among viewers).
    useEffect(() => {
        if (!editor || !collab || !collab.synced || readOnly) return;
        const { provider, fragment, field } = collab;
        provider.setAwarenessField("sectionField", field);

        const doc = fragment.doc;
        const awareness = provider.awareness;
        if (!doc || !awareness) return;

        const meta = doc.getMap<boolean>("collabMeta");
        const seededKey = `seeded:${field}`;
        let timer: ReturnType<typeof setTimeout> | null = null;
        let cancelled = false;

        const attempt = () => {
            if (cancelled) return;
            if (meta.get(seededKey) || fragment.length > 0) return; // done or legacy content
            if (electSeeder(awareness.getStates(), awareness.clientID, field)) {
                editor.commands.setContent(parseContent(value), false);
                meta.set(seededKey, true);
                backfillBridgeNodeIds(editor);
                return;
            }
            timer = setTimeout(attempt, 400); // elected peer should set the flag shortly
        };
        attempt();

        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
        // `value` is intentionally not a dep: seeding uses the HTML loaded at mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor, collab, readOnly]);

    // Click delegation for staged agent suggestions. Three targets:
    //   • [data-suggestion-accept] — green chip or inline ✓ button (widget decoration)
    //   • [data-suggestion-reject] — hover ✕ button (widget decoration)
    //   • span[data-agent-suggestion] — the red strikethrough mark itself; plain click
    //     accepts, Alt+click rejects (legacy/keyboard-free fallback).
    const handleEditorMouseDown = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            if (readOnly) return;
            const target = e.target as HTMLElement;

            const acceptEl = target.closest<HTMLElement>("[data-suggestion-accept]");
            if (acceptEl) {
                const editId = acceptEl.getAttribute("data-edit-id") ?? "";
                if (editId) {
                    e.preventDefault();
                    e.stopPropagation();
                    bridge.adapter.resolve(editId, "accepted");
                    return;
                }
            }

            const rejectEl = target.closest<HTMLElement>("[data-suggestion-reject]");
            if (rejectEl) {
                const editId = rejectEl.getAttribute("data-edit-id") ?? "";
                if (editId) {
                    e.preventDefault();
                    e.stopPropagation();
                    bridge.adapter.resolve(editId, "rejected");
                    return;
                }
            }

            const span = target.closest<HTMLElement>("span[data-agent-suggestion]");
            if (!span) return;
            const editId = span.getAttribute("data-edit-id") ?? "";
            if (!editId) return;
            e.preventDefault();
            e.stopPropagation();
            bridge.adapter.resolve(editId, e.altKey ? "rejected" : "accepted");
        },
        [bridge, readOnly]
    );

    // Update editor content when value prop changes.
    //
    // We compare the CLEANED HTML (with agent staging artifacts stripped)
    // against the incoming value. If they match, the only thing that changed
    // is local staging (suggestion marks / blocks) — those are ephemeral and
    // must NOT trigger a setContent reset, otherwise the round-trip
    // (stage -> onChange(clean) -> parent updates value -> useEffect resets)
    // wipes the staging within one render cycle.
    useEffect(() => {
        if (collab) return; // Yjs owns content; setContent would fight the CRDT
        if (editor && value !== undefined) {
            const refreshTarget = sectionId !== undefined
                ? bridge.adapter.getEditorForSection(sectionId) ?? editor
                : editor;
            const currentClean = getCleanHtml(refreshTarget);
            if (currentClean !== value) {
                // Real content change from outside (server push, another
                // editor, undo of accepted edits). Re-parse and reset.
                const html = parseContent(value);
                console.log("[TiptapEditor] Setting editor content, first 300 chars:", html.substring(0, 300));
                refreshTarget.commands.setContent(html, false);
                backfillBridgeNodeIds(refreshTarget);
            }
        }
    }, [editor, value, collab, sectionId, bridge]);

    if (!editor) {
        return <div>Loading editor...</div>;
    }

    return (
        <Box
            className="tiptap-editor-wrapper"
            onFocusCapture={() => setFocused(true)}
            onBlurCapture={event => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocused(false);
            }}
            onInputCapture={() => { if (!readOnly) setFocused(true); }}
            sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden"
            }}
        >
            {/* Toolbar */}
            {!readOnly && <TiptapToolbar
                editor={editor}
                projectId={projectId}
                featureId={featureId}
                userStoryId={userStoryId}
                sectionId={sectionId}
                workProductTitle={workProductTitle}
                hasJiraLink={hasJiraLink}
                hasAzureDevOpsLink={hasAzureDevOpsLink}
                needsRebuild={needsRebuild}
                onRebuild={onRebuild}
                workProductId={workProductId}
                allSections={allSections}
                onNavigateToIntegrations={onNavigateToIntegrations}
            />}

            {/* Editor Content */}
            <Box
                data-collab-scroll
                sx={{
                    flex: 1,
                    overflow: "auto",
                    padding: "16px",
                    backgroundColor: "#fff"
                }}
                onMouseDownCapture={handleEditorMouseDown}
            >
                <EditorContent editor={editor} />
            </Box>
        </Box>
    );
}
